import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { Mail, Zap, Megaphone, Heart, ExternalLink, Loader2, CheckCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const Advertise = () => {
  const [loadingPayment, setLoadingPayment] = useState(null);
  const [donationAmount, setDonationAmount] = useState(25);
  const [selectedWeeks, setSelectedWeeks] = useState([]);  // Array of week dates
  const [bookedWeeks, setBookedWeeks] = useState([]);  // Already booked weeks
  const [searchParams] = useSearchParams();
  
  // Handle Stripe redirect back
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Payment successful! Your ad or donation is being processed. Thank you!');
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Payment canceled. No charges were made.');
    }
  }, [searchParams]);

  // Fetch already booked weeks
  useEffect(() => {
    const fetchBookedWeeks = async () => {
      try {
        const response = await api.get('/ad-bookings/main-sponsor');
        setBookedWeeks(response.data?.booked_weeks || []);
      } catch (error) {
        console.log('Could not fetch booked weeks');
      }
    };
    fetchBookedWeeks();
  }, []);
  
  // Generate next 12 weeks
  const getNext12Weeks = () => {
    const weeks = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + (i * 7));
      weeks.push({
        start: weekStart,
        label: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: weekStart.toISOString().split('T')[0]
      });
    }
    return weeks;
  };

  const availableWeeks = getNext12Weeks();

  const toggleWeek = (weekDate) => {
    // Don't allow selecting already booked weeks
    if (bookedWeeks.includes(weekDate)) {
      toast.error('This week is already booked');
      return;
    }
    
    if (selectedWeeks.includes(weekDate)) {
      setSelectedWeeks(selectedWeeks.filter(w => w !== weekDate));
    } else {
      setSelectedWeeks([...selectedWeeks, weekDate]);
    }
  };

  const handleAdPurchase = async (adType) => {
    if (adType === 'main_sponsor' && selectedWeeks.length === 0) {
      toast.error('Please select at least one week to book');
      return;
    }
    
    setLoadingPayment(adType);
    try {
      const response = await api.post('/billing/start-ad', null, {
        params: { 
          ad_type: adType,
          weeks: adType === 'main_sponsor' ? selectedWeeks.length : 1
        }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoadingPayment(null);
    }
  };

  const handleDonation = async (amount) => {
    setLoadingPayment('donation');
    try {
      const response = await api.post('/billing/start-donation', null, {
        params: { amount }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process donation. Please try again.');
      setLoadingPayment(null);
    }
  };

  return (
    <Layout>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="font-headline text-4xl md:text-5xl text-white mb-4">ADVERTISE WITH US</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Connect with engaged citizens who care about their community through targeted, meaningful advertising.
          </p>
        </div>

        {/* Ad Packages */}
        <div className="space-y-6 mb-12">
          
          {/* Main Site Sponsor - NEW */}
          <div className="bg-gradient-to-r from-[#C5A52A]/20 to-[#C5A52A]/5 border-2 border-[#C5A52A] rounded-sm p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-[#C5A52A] text-black text-xs font-bold px-3 py-1 rounded uppercase">
              FEATURED
            </div>
            <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-wider flex items-center gap-3">
              <Zap className="w-6 h-6 text-[#C5A52A]" />
              Main Site Sponsor
            </h3>
            <p className="text-gray-400 mb-4">
              Your brand at the top of every page, right below our headline. Maximum visibility with exclusive placement.
            </p>
            
            <div className="bg-[#111] border border-[#2a2a2a] rounded p-4 mb-6">
              <p className="font-mono text-xs text-[#C5A52A] mb-2">RECOMMENDED BANNER SIZE:</p>
              <p className="font-display text-white text-lg">1200 × 200 pixels</p>
              <p className="font-mono text-xs text-gray-500 mt-1">Supports: JPG, PNG, GIF (animated), WebP</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-[#C5A52A] font-bold mb-1">✓ Top-of-Page Placement</div>
                <p className="text-gray-500 text-sm">Visible on every page load</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-[#C5A52A] font-bold mb-1">✓ Exclusive Spot</div>
                <p className="text-gray-500 text-sm">Only one sponsor at a time</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-[#C5A52A] font-bold mb-1">✓ GIF Support</div>
                <p className="text-gray-500 text-sm">Animated banners welcome</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-[#C5A52A] font-bold mb-1">✓ Weekly Billing</div>
                <p className="text-gray-500 text-sm">*Free ad with 4+ weeks purchased</p>
              </div>
            </div>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div className="flex-1">
                <div className="text-[#C5A52A] text-3xl font-black mb-2">
                  ${100 * selectedWeeks.length} {selectedWeeks.length > 0 && `(${selectedWeeks.length} week${selectedWeeks.length > 1 ? 's' : ''})`}
                </div>
                <p className="text-gray-500 text-sm mb-3">Click weeks below to select your booking dates:</p>
                
                {/* Week Picker Calendar */}
                <div className="bg-[#111] border border-[#2a2a2a] rounded p-4">
                  <div className="grid grid-cols-3 gap-2">
                    {availableWeeks.map((week, idx) => {
                      const isSelected = selectedWeeks.includes(week.fullDate);
                      const isBooked = bookedWeeks.includes(week.fullDate);
                      return (
                        <button
                          key={idx}
                          onClick={() => toggleWeek(week.fullDate)}
                          disabled={isBooked}
                          className={`p-3 rounded text-sm font-mono transition-all ${
                            isBooked
                              ? 'bg-gray-900 text-gray-600 border border-gray-800 cursor-not-allowed line-through opacity-50'
                              : isSelected 
                                ? 'bg-[#C5A52A] text-black border-2 border-[#C5A52A]' 
                                : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-[#C5A52A]'
                          }`}
                        >
                          <div className="text-xs opacity-70">{isBooked ? 'BOOKED' : `Week ${idx + 1}`}</div>
                          <div className="font-bold">{week.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedWeeks.length >= 4 && (
                    <p className="text-green-500 text-sm mt-3 font-mono text-center">
                      💰 {selectedWeeks.length}+ weeks selected! Message us for a bonus standard ad!
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleAdPurchase('main_sponsor')}
                disabled={loadingPayment === 'main_sponsor' || selectedWeeks.length === 0}
                className="bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 disabled:cursor-not-allowed text-black font-bold py-3 px-8 rounded-sm uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                {loadingPayment === 'main_sponsor' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Book Now
              </button>
            </div>
          </div>

          {/* Email Tracker Sponsor */}
          <div className="bg-gradient-to-r from-purple-900/20 to-purple-800/10 border-2 border-purple-600 rounded-sm p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded uppercase">
              PREMIUM
            </div>
            <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-wider flex items-center gap-3">
              <Mail className="w-6 h-6 text-purple-400" />
              Email Tracker Sponsor
            </h3>
            <p className="text-gray-400 mb-4">
              Exclusive sponsorship of our councillor communication system. Your logo appears on email notifications (optional) and message tracking pages.
            </p>
            
            <div className="bg-yellow-900/20 border border-yellow-600/30 rounded p-3 mb-4">
              <p className="text-yellow-400 text-sm">
                <strong>Note:</strong> Logo inclusion in emails to councillors is <strong>optional</strong>. 
                Sponsors can choose anonymous branding if preferred.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-purple-400 font-bold mb-1">✓ Exclusive Placement</div>
                <p className="text-gray-500 text-sm">Only one sponsor at a time</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-purple-400 font-bold mb-1">✓ Email Notifications</div>
                <p className="text-gray-500 text-sm">Logo optional in councillor emails</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-purple-400 font-bold mb-1">✓ Dashboard Placement</div>
                <p className="text-gray-500 text-sm">Visible on user message tracking</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-purple-400 font-bold mb-1">✓ Flexible Branding</div>
                <p className="text-gray-500 text-sm">Anonymous option available</p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-purple-400 text-3xl font-black">$500/month</div>
                <p className="text-gray-500 text-sm">Minimum 3-month commitment</p>
              </div>
              <button
                onClick={() => handleAdPurchase('email_sponsor')}
                disabled={loadingPayment === 'email_sponsor'}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white font-bold py-3 px-8 rounded-sm uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                {loadingPayment === 'email_sponsor' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Get Started
              </button>
            </div>
          </div>

          {/* Standard Ad */}
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-8">
            <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-wider flex items-center gap-3">
              <Megaphone className="w-6 h-6 text-green-400" />
              Standard Feed Ad
            </h3>
            <p className="text-gray-400 mb-4">
              Your ad appears between posts on our community feed, reaching residents actively engaged with local issues.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-green-500 font-bold mb-1">✓ Feed Integration</div>
                <p className="text-gray-500 text-sm">Every 3rd post</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-green-500 font-bold mb-1">✓ Custom Creative</div>
                <p className="text-gray-500 text-sm">Image, text, and link</p>
              </div>
              <div className="bg-gray-950 p-4 rounded">
                <div className="text-green-500 font-bold mb-1">✓ Geo-Targeted</div>
                <p className="text-gray-500 text-sm">Regina residents only</p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-green-500 text-3xl font-black">$40/month</div>
                <p className="text-gray-500 text-sm">No minimum commitment</p>
              </div>
              <button
                onClick={() => handleAdPurchase('standard')}
                disabled={loadingPayment === 'standard'}
                className="bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-black font-bold py-3 px-8 rounded-sm uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                {loadingPayment === 'standard' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Get Started
              </button>
            </div>
          </div>

          {/* Donation Option */}
          <div className="bg-gray-900 border border-green-800 rounded-sm p-8">
            <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-wider flex items-center gap-3">
              <Heart className="w-6 h-6 text-red-400" />
              Support Our Platform
            </h3>
            <p className="text-gray-400 mb-6">
              Help us keep Regina's civic engagement platform free and accessible for all residents. Your donation directly supports our mission of government transparency and community empowerment.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[10, 25, 50, 100].map((amount) => (
                <button
                  key={amount}
                  onClick={() => setDonationAmount(amount)}
                  className={`p-4 rounded-sm transition-colors ${
                    donationAmount === amount
                      ? 'bg-green-600 border-2 border-green-400'
                      : 'bg-gray-950 border border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className={`font-bold text-lg mb-1 ${donationAmount === amount ? 'text-white' : 'text-white'}`}>
                    ${amount}
                  </div>
                  <div className="text-gray-500 text-xs">One-time</div>
                </button>
              ))}
              <div className="p-4 bg-gray-950 border border-gray-700 rounded-sm">
                <input
                  type="number"
                  min="1"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(parseInt(e.target.value) || 1)}
                  className="w-full bg-transparent text-white font-bold text-lg text-center border-none focus:outline-none"
                  placeholder="$"
                />
                <div className="text-gray-500 text-xs text-center">Custom</div>
              </div>
            </div>
            <div className="text-center">
              <button
                onClick={() => handleDonation(donationAmount)}
                disabled={loadingPayment === 'donation'}
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-black font-bold py-3 px-8 rounded-sm uppercase tracking-wider transition-colors"
              >
                {loadingPayment === 'donation' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Heart className="w-4 h-4" />
                )}
                Donate ${donationAmount}
              </button>
            </div>
          </div>
        </div>

        {/* Why Advertise */}
        <div className="bg-gray-900 border border-gray-800 rounded-sm p-8 mb-12">
          <h3 className="text-xl font-black text-white mb-6 uppercase tracking-wider">
            Why Advertise on Our Platform?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-green-500 font-bold mb-2">🎯 Highly Engaged Audience</h4>
              <p className="text-gray-400 text-sm">
                Reach Regina residents who are actively involved in their community and local government.
              </p>
            </div>
            <div>
              <h4 className="text-green-500 font-bold mb-2">🏘️ Local Focus</h4>
              <p className="text-gray-400 text-sm">
                100% Regina-based audience means your ads reach the right people at the right time.
              </p>
            </div>
            <div>
              <h4 className="text-green-500 font-bold mb-2">📊 Measurable Results</h4>
              <p className="text-gray-400 text-sm">
                Track clicks, impressions, and engagement with detailed monthly reports.
              </p>
            </div>
            <div>
              <h4 className="text-green-500 font-bold mb-2">🤝 Support Transparency</h4>
              <p className="text-gray-400 text-sm">
                Your ad dollars support civic engagement and government accountability.
              </p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center bg-gray-950 border border-gray-800 rounded-sm p-8">
          <h3 className="text-xl font-black text-white mb-4">Have Questions?</h3>
          <p className="text-gray-400 mb-6">
            We'd love to discuss how we can help your business or organization reach Regina residents.
          </p>
          <a
            href="mailto:ads@accountability.works"
            className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-sm uppercase tracking-wider transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contact Us: ads@accountability.works
          </a>
        </div>
      </main>
    </Layout>
  );
};

export default Advertise;
